/** * Calculates the actual font size and descent based on the provided font size in pixels, scaling factor, and font name.
 *
 * @param {number} fontSizePx - The font size in pixels.
 * @param {number} scalingFactor - The scaling factor to apply to the calculated size and descent.
 * @param {string} fontName - The name of the font to use for measurements.
 * @returns {{actualSize: number, descent: number}} An object containing the actual font size and descent, both scaled by the provided factor.
 */
function calculateActualSize(fontSizePx, scalingFactor, fontName, measurementChar = 'I') {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `${fontSizePx}px ${fontName}`;

    const metrics = ctx.measureText(measurementChar);
    const actualBoundingBoxAscent = metrics.actualBoundingBoxAscent || 0;

    const actualSize = ( fontSizePx * fontSizePx ) / actualBoundingBoxAscent

    ctx.font = `${actualSize}px ${fontName}`;
    const metrics2 = ctx.measureText(measurementChar);
    const descent = metrics2.ideographicBaseline || 0;
    //some fonts don't have proper metrics, so we need to add backup for descent
    const descent2 = (actualSize - metrics2.hangingBaseline )* -1;
    //destroy canvas
    canvas.remove();

    return  {
        actualSize : actualSize * scalingFactor,
        descent : descent * scalingFactor,
        descent2 : descent2 * scalingFactor
    }
}

/**
 * Picks a measurement character guaranteed to exist in the target font variant.
 * Subset fonts (e.g. numbers-only) cause canvas measureText to silently fall back
 * to a system font for missing glyphs, producing incorrect ascent metrics.
 * Priority: big letters → small letters → numbers → specials.
 */
function pickMeasurementChar(allowedFlags) {
    if (!allowedFlags) return 'I';
    if (allowedFlags.allowedBigLetters) return 'I';
    if (allowedFlags.allowedSmallLetters) return 'a';
    if (allowedFlags.allowedNumbers) return '1';
    if (allowedFlags.allowedSpecial) return '+';
    return 'I';
}

/**
 * Funkcja do konwersji centymetrów na piksele.
 * @param cm
 * @returns {number}
 */
function convertCmToPx(cm) {
  const pxPerCm = 37.7952755906; // 1 cm = 37.7952755906 pixels
  return cm * pxPerCm;
}

/**
 * Funkcja do konwersji centymetrów na piksele z uwzględnieniem współczynnika skalowania.
 * @param cm
 * @param scalingFactor
 * @returns {number}
 */
function convertCmToPxWithScaling(cm , scalingFactor) {
    const px = convertCmToPx(cm);
    return px * scalingFactor;
}

/**
 * Funkcja do konwersji pikseli na centymetry.
 * @param px
 * @returns {number}
 */
function convertPxToCm(px) {
    const cmPerPx = 1 / 37.7952755906; // 1 pixel = 0.0264583333 cm
    return px * cmPerPx;
}

/**
 * Funkcja do konwersji pikseli na centymetry z uwzględnieniem współczynnika skalowania.
 * @param px
 * @param scalingFactor
 * @returns {number}
 */
function convertPxToCmWithScaling(px, scalingFactor) {
    const cm = convertPxToCm(px);
    return cm / scalingFactor;
}

/**
 * Funkcja do konwersji milimetrów na piksele.
 * @param mm
 * @returns {number}
 */
function convertMMtoPx(mm) {
    const pxPerMM = 3.77952755906; // 1 mm = 3.77952755906 pixels
    return mm * pxPerMM;
}

/**
 * Funkcja do konwersji milimetrów na piksele z uwzględnieniem współczynnika skalowania.
 * @param mm
 * @param scalingFactor
 * @returns {number}
 */
function convertMMtoPxWithScaling(mm, scalingFactor) {
    const pxPerMM = 3.77952755906; // 1 mm = 3.77952755906 pixels
    const px = convertMMtoPx(mm);
    return px * scalingFactor;
}

/**
 * Funkcja pomocnicza do sprawdzenia, czy wartość jest liczbą.
 * @param value
 * @returns {boolean}
 */
function isNumber(value) {
    if (typeof value !== 'string') return false;
    // Usuwa białe znaki z początku i końca
    const trimmed = value.trim();
    // Sprawdza, czy nie jest pusty i czy jest liczbą
    return trimmed !== '' && !isNaN(Number(trimmed));
}

/**
 * Sprawdza, czy dany znak jest dozwolony w kontekście konfiguracji.
 * @param letter
 * @param configContext
 * @returns {boolean}
 */
function checkIfSingIsAllowed(letter, configContext) {
    const allowedSpecialChars = ['+', '*'];

    // Sprawdź czy letter jest cyfrą
    if (isNumber(letter)) {
        return !!configContext.allowedNumbers;
    }

    // Sprawdź czy letter jest znakiem specjalnym
    if (allowedSpecialChars.includes(letter)) {
        return !!configContext.allowedSpecial;
    }

    // Sprawdź czy letter jest małą literą (ale nie cyfrą ani znakiem specjalnym)
    if (
        letter === letter.toLowerCase() &&
        letter !== letter.toUpperCase()
    ) {
        return !!configContext.allowedSmallLetters;
    }

    // Sprawdź czy letter jest dużą literą (ale nie cyfrą ani znakiem specjalnym)
    if (
        letter === letter.toUpperCase() &&
        letter !== letter.toLowerCase()
    ) {
        return !!configContext.allowedBigLetters;
    }

    // Jeśli żaden warunek nie jest spełniony
    return false;
}

/**
 * Funkcja walidująca wszystkie litery w treści
 */
const validateAllLetters = (content, configContext) => {
    let result = true;

    if(!content || content.length === 0) {
        return true; // Jeśli treść jest pusta, uznajemy, że jest poprawna
    }

    for (let i = 0; i < content.length; i++) {
        const char = content[i];
        // Wyklucz spację z walidacji
        if (char === ' ') continue;
        if (!checkIfSingIsAllowed(char, configContext)) {
            result = false;
            break;
        }
    }
    return result;
};

/**
 * Calculates grid line positions based on canvas dimensions and grid spacing.
 * @param {number} canvasWidthCm - Canvas width in centimeters
 * @param {number} canvasHeightCm - Canvas height in centimeters
 * @param {number} gridSpacingCm - Grid spacing in centimeters
 * @returns {{verticalLines: number[], horizontalLines: number[]}} - Arrays of line positions in pixels
 */
function calculateGridLines(canvasWidthCm, canvasHeightCm, gridSpacingCm) {
    const verticalLines = [];
    const horizontalLines = [];

    // Calculate number of vertical lines (along width)
    const numVerticalLines = Math.floor(canvasWidthCm / gridSpacingCm);
    for (let i = 0; i <= numVerticalLines; i++) {
        verticalLines.push(convertCmToPx(i * gridSpacingCm));
    }

    // Calculate number of horizontal lines (along height)
    const numHorizontalLines = Math.floor(canvasHeightCm / gridSpacingCm);
    for (let i = 0; i <= numHorizontalLines; i++) {
        horizontalLines.push(convertCmToPx(i * gridSpacingCm));
    }

    return { verticalLines, horizontalLines };
}

/**
 * Returns the snap-to-grid value in pixels based on grid spacing in centimeters.
 * @param {number} gridSpacingCm - Grid spacing in centimeters
 * @returns {number} - Snap value in pixels
 */
function getGridSnapValue(gridSpacingCm) {
    return convertCmToPx(gridSpacingCm);
}

/**
 * Calculates the aligned position for a text section based on alignment type.
 * @param {string} alignment - Alignment type: 'left', 'center', or 'right'
 * @param {number} textSectionWidth - Width of text section element in pixels
 * @param {number} canvasWidth - Width of canvas from WorkingSizeContext.widthPx
 * @param {number} currentY - Current y-coordinate to preserve
 * @param {number} dragSnapSpacingCm - Grid snap spacing in centimeters
 * @param {number} [extraMarginPx=0] - Optional extra margin in pixels to add to the final x position
 * @returns {{x: number, y: number}} - New position with grid-snapped coordinates
 */
function calculateAlignmentPosition(alignment, textSectionWidth, canvasWidth, currentY, dragSnapSpacingCm, extraMarginPx) {
    let rawX = 0;

    // Calculate raw x position based on alignment
    switch (alignment) {
        case 'left':
            rawX = 0;
            break;
        case 'center':
            rawX = (canvasWidth - textSectionWidth) / 2;
            break;
        case 'right':
            rawX = canvasWidth - textSectionWidth;
            break;
        default:
            rawX = 0;
    }

    // Apply grid snapping
    const snapValue = getGridSnapValue(dragSnapSpacingCm);
    const snappedX = Math.round(rawX / snapValue) * snapValue;

    // Apply boundary constraints
    let finalX = snappedX;
    if (finalX < 0) {
        finalX = 0;
    }
    if (finalX + textSectionWidth > canvasWidth) {
        finalX = Math.max(0, canvasWidth - textSectionWidth);
    }

    return {
        x: finalX + extraMarginPx,
        y: currentY
    };
}

export {
    convertCmToPx,
    convertCmToPxWithScaling,
    convertPxToCm,
    convertPxToCmWithScaling,
    convertMMtoPxWithScaling,
    convertMMtoPx,
    isNumber,
    checkIfSingIsAllowed,
    validateAllLetters,
    calculateGridLines,
    getGridSnapValue,
    calculateAlignmentPosition,
    calculateActualSize,
    pickMeasurementChar
};